import { useDroppable } from "@dnd-kit/core";
import { FileText, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PipelineRow } from "@/types/pipeline";

interface KanbanColumnProps {
  pipeline: PipelineRow;
  count: number;
  children: React.ReactNode;
  onAddDeal?: (pipelineId: string) => void;
}

export function KanbanColumn({ pipeline, count, children, onAddDeal }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column:${pipeline.id}`,
    data: { pipelineId: pipeline.id, pipeline },
  });

  return (
    <div
      className={cn(
        "flex h-full w-[290px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card",
        isOver && "ring-2 ring-primary/30",
      )}
      aria-label={`${pipeline.title} column with ${count} deals`}
    >
      {/* Column header */}
      <div className="flex flex-shrink-0 items-center justify-between gap-2 px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="h-2 w-2 flex-shrink-0 rounded-full"
            style={{ backgroundColor: pipeline.color ?? "hsl(var(--muted-foreground))" }}
            aria-hidden
          />
          <span className="truncate text-[13px] font-semibold text-foreground">
            {pipeline.title}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="min-w-[22px] text-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
            {count}
          </span>
          {onAddDeal && (
            <button
              type="button"
              onClick={() => onAddDeal(pipeline.id)}
              className="grid h-[22px] w-[22px] place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              aria-label={`Add deal to ${pipeline.title}`}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {/* Cards area */}
      <div
        ref={setNodeRef}
        className={cn(
          "flex flex-1 flex-col gap-2 overflow-y-auto p-2",
          isOver && "bg-primary/[0.03]",
        )}
      >
        {count === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-2 text-muted-foreground/60 py-8">
            <FileText className="h-5 w-5" aria-hidden />
            <span className="text-[12px]">No deals yet</span>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
